import { CAMPAIGN_TABS } from '../../campaign-detail/search';

export interface NotificationTargetInput {
  category?: 'message' | 'activity';
  campaign_id?: string;
  thread_type?: 'selection' | 'script' | 'content';
  creator_username?: string;
  type?: string;
  count?: number;
  deep_link?: string;
}

const MESSAGE_TARGETS = {
  selection: { tab: 'creators', panel: 'conversation' },
  script: { tab: 'scripts', panel: 'script-review', thread: 'open' },
  content: { tab: 'content', panel: 'content-review', thread: 'open' },
} as const;

/** Review-panel target for an activity `type`, keyed off the same
 * script/content substring checks used elsewhere in the notification UI.
 * Returns null for types with no dedicated review panel (joined/accepted/
 * live etc.) — callers fall back to the generic `deep_link` tab for those. */
function activityPanelForType(type: string): { tab: string; panel: string } | null {
  if (type.includes('script_review') || type.includes('script_revised')) {
    return { tab: 'scripts', panel: 'script-review' };
  }
  if (type.includes('content_review') || type.includes('content_revised')) {
    return { tab: 'content', panel: 'content-review' };
  }
  return null;
}

/** A `deep_link` the backend wrote as a full admin path (outreach alerts,
 *  Section F): `/campaign/<slug-or-id>?…` or `/settings?…`. Also reads the two
 *  shapes written before 2.8, which coalesced notifications keep (the
 *  upsert never replaces a stored deep_link): `/campaigns/<id>?…` and
 *  `/settings/outreach[?tab=<sub>]`. null for a bare tab name (every other
 *  notification) or any other path. */
export function parseFullDeepLink(
  deepLink: string | undefined,
  campaignRouteBase = '/campaign',
): { to: string; search: Record<string, string> } | null {
  if (!deepLink?.startsWith('/')) return null;
  const url = new URL(deepLink, 'https://deep-link.invalid');
  const search = Object.fromEntries(url.searchParams.entries());
  const [head, param, ...rest] = url.pathname.split('/').filter(Boolean);
  if (rest.length > 0) return null;
  if ((head === 'campaign' || head === 'campaigns') && param) {
    return { to: `${campaignRouteBase}/${param}`, search };
  }
  if (head === 'settings' && !param) return { to: '/settings', search };
  if (head === 'settings' && param === 'outreach') {
    const { tab: legacySub, ...others } = search;
    return { to: '/settings', search: { ...others, tab: 'outreach', ...(legacySub ? { sub: legacySub } : {}) } };
  }
  return null;
}

const isEntryRollbackType = (type: string) => type.startsWith('roster.entry.') && type.endsWith('_rolled_back');

const isCampaignTab = (tab: string | undefined): tab is string =>
  !!tab && (CAMPAIGN_TABS as readonly string[]).includes(tab);

/** Maps a notification (panel row or SSE toast payload) to the campaign-detail deep link it should open. */
export function buildNotificationTarget(
  n: NotificationTargetInput,
  campaignRouteBase = '/campaign',
): { to: string; search: Record<string, string> } | null {
  if (n.type === 'campaign_grant.revoked') {
    return { to: '/campaigns', search: {} };
  }
  if (n.type === 'member.role_changed') {
    return { to: '/settings', search: {} };
  }
  if (n.type === 'campaign_grant.assigned' || n.type === 'campaign_grant.changed') {
    // These fall through to the generic ?tab= branch otherwise, which the
    // backend sets to 'settings' (the Access-Control tab) — a valid tab so
    // it doesn't get dropped, but it lands a freshly-granted (possibly
    // lower-level) member on a tab they may not be able to see, instead of
    // the campaign itself.
    if (!n.campaign_id) {
      // A campaign_grant notification with no campaign_id is a backend bug
      // (the grant it's about can't be identified) — both consumers
      // (NotificationBell, NotificationToastBridge) mark it read before
      // checking whether a target resolved, so without this the click
      // silently does nothing and leaves no trace.
      console.error('[Notifications] unresolvable target — campaign_grant notification missing campaign_id', n);
      return null;
    }
    return { to: `${campaignRouteBase}/${n.campaign_id}`, search: {} };
  }
  if (n.type === 'shopify.store_access_lost' || (!n.campaign_id && n.deep_link === 'integrations')) {
    return { to: '/integrations', search: {} };
  }
  const full = parseFullDeepLink(n.deep_link, campaignRouteBase);
  if (full) return full;
  if (!n.campaign_id) return null;
  const to = `${campaignRouteBase}/${n.campaign_id}`;
  const u = n.creator_username;

  if (n.category === 'message' && n.thread_type && u) {
    const t = MESSAGE_TARGETS[n.thread_type];
    const search: Record<string, string> = { tab: t.tab, panel: t.panel, creator: u };
    if ('thread' in t) search.thread = t.thread;
    return { to, search };
  }
  if (n.category === 'activity' && (n.count ?? 1) === 1 && u && n.type) {
    const panel = activityPanelForType(n.type);
    if (panel) return { to, search: { tab: panel.tab, panel: panel.panel, creator: u } };
    if (isEntryRollbackType(n.type) && isCampaignTab(n.deep_link)) return { to, search: { tab: n.deep_link, creator: u } };
  }
  const search: Record<string, string> = {};
  if (n.deep_link && (CAMPAIGN_TABS as readonly string[]).includes(n.deep_link)) search.tab = n.deep_link;
  return { to, search };
}

/** Maps a single actor row inside a batched-activity card (see
 * `BatchedActivityCard` in NotificationBell) to the campaign-detail deep
 * link it should open — the per-type logic mirrors the single-activity
 * branch of `buildNotificationTarget` above, but with an explicit
 * `username` instead of relying on `n.creator_username` (a batched
 * notification's top-level `creator_username` doesn't identify any one
 * actor in the group). Joined/accepted/live types have no dedicated review
 * panel, so they fall back to the notification's `deep_link` tab with the
 * actor focused via `creator` — same generic tab-support guard as the
 * fallback path above. */
export function buildBatchActorTarget(
  n: NotificationTargetInput,
  username: string,
  campaignRouteBase = '/campaign',
): { to: string; search: Record<string, string> } | null {
  const full = parseFullDeepLink(n.deep_link, campaignRouteBase);
  if (full) return full.to === '/settings' ? full : { to: full.to, search: { ...full.search, creator: username } };
  if (!n.campaign_id) return null;
  const to = `${campaignRouteBase}/${n.campaign_id}`;

  const panel = n.type ? activityPanelForType(n.type) : null;
  if (panel) return { to, search: { tab: panel.tab, panel: panel.panel, creator: username } };

  if (n.deep_link && (CAMPAIGN_TABS as readonly string[]).includes(n.deep_link)) {
    return { to, search: { tab: n.deep_link, creator: username } };
  }
  return { to, search: {} };
}
